"use client";
import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

const PRICES = {
  TRAINING: 39,
  FOOD: 39,
  BUNDLE: 59,
};
const COUPON = "START10";

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    plan: "TRAINING",
    coupon: "",
    company: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState("");

  // Auto-apply discount code from URL
  useEffect(() => {
    const discountCode = searchParams.get('discount');
    if (discountCode) {
      // Batch state updates to reduce layout thrashing
      requestAnimationFrame(() => {
        const code = discountCode.trim().toUpperCase();
        setFormData((prev) => ({ ...prev, coupon: code }));
        
        if (code === COUPON) {
          setCouponApplied(true);
        } else {
          setCouponError("Ongeldige kortingscode.");
        }
      });
    }
  }, []);

  // Send page visited event
  useEffect(() => {
    const email = searchParams.get('email');
    if (email) {
      // Send page visited event to Make webhook
      axios.post("https://hook.eu2.make.com/u1oqnv235g13xus4hhxemo5t2dn1kp93", {
        email: email.trim(),
        eventType: "page_visited",
        page: "contact",
        visitedAt: new Date().toISOString(),
      }).catch(err => {
        console.error("Failed to send page visit event:", err);
      });
    }
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((p) => ({ ...p, [id]: value }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: undefined }));
    if (id === "plan") {
      // changing plan shouldn't keep plan-specific validation errors
      setCouponError("");
    }
  };

  const price = useMemo(() => {
    switch (formData.plan) {
      case "TRAINING":
        return PRICES.TRAINING;
      case "FOOD":
        return PRICES.FOOD;
      case "BUNDLE":
        return PRICES.BUNDLE;
      default:
        return 0;
    }
  }, [formData.plan]);

  const discount = useMemo(() => {
    if (!couponApplied) return 0;
    if (formData.plan === "BUNDLE") return 20; // START10 = -20 for bundle
    return 10; // single plans
  }, [couponApplied, formData.plan]);

  const total = Math.max(0, price - discount);

  const applyCoupon = (e) => {
    e.preventDefault();
    setCouponError("");
    const code = (formData.coupon || "").trim().toUpperCase();
    if (!code) {
      setCouponApplied(false);
      return;
    }
    if (code === COUPON) {
      setCouponApplied(true);
    } else {
      setCouponApplied(false);
      setCouponError("Ongeldige kortingscode.");
    }
  };

  const validate = () => {
    const v = {};
    if (!formData.name.trim()) v.name = "Vul je naam in.";
    if (!formData.email.trim()) {
      v.email = "Vul je e-mail in.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      v.email = "Vul een geldig e-mailadres in.";
    }
    // if (!formData.message.trim()) v.message = "Schrijf een bericht.";
    if (!["TRAINING", "FOOD", "BUNDLE"].includes(formData.plan)) v.plan = "Kies een optie.";
    return v;
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
  
    // Honeypot
    if (formData.company) {
      setSubmitStatus("success");
      return;
    }
  
    const v = validate();
    if (Object.keys(v).length) {
      setErrors(v);
      return;
    }
  
    setIsSubmitting(true);
    try {
      await axios.post("https://hook.eu2.make.com/u1oqnv235g13xus4hhxemo5t2dn1kp93", {
        name: formData.name,
        email: formData.email,
        message: formData.message,
        selectedPlan: formData.plan,
        basePrice: price,
        couponEntered: formData.coupon.trim(),
        couponApplied,
        discount,
        total,
        source: "contact-page",
        submittedAt: new Date().toISOString(),
      });
      
      // Wrap state updates in setTimeout to avoid ResizeObserver issues
      setTimeout(() => {
        setSubmitStatus("success");
        setFormData({
          name: "",
          email: "",
          message: "",
          plan: "TRAINING",
          coupon: "",
          company: "",
        });
        setCouponApplied(false);
        setCouponError("");
      }, 0);
    } catch (err) {
      console.error(err);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>

      {/* Form + Order Card */}
      <section className="container mx-auto px-4 pb-[60px] md:pb-[100px]">
        <div className="grid grid-cols-12 gap-[20px] md:gap-[50px]">
          {/* Form */}
          <div className="col-span-12 md:col-span-8">
            <div className="">
              <h2 className="text-2xl font-bold text-black mb-2">Stel je vraag</h2>
              <p className="text-gray-700 mb-6">
                Vul je gegevens in en vertel kort waar ik je mee kan helpen.
              </p>

              {/* Status */}
              {submitStatus === "success" && (
                <div className="mb-6 rounded border border-green-200 bg-green-50 p-4 text-green-700" role="status" aria-live="polite">
                  Bedankt! Je bericht is succesvol verzonden.
                </div>
              )}
              {submitStatus === "error" && (
                <div className="mb-6 rounded border border-red-200 bg-red-50 p-4 text-red-700" role="status" aria-live="assertive">
                  Oeps! Er ging iets mis. Probeer het later opnieuw.
                </div>
              )}

              <form onSubmit={submitForm} noValidate>
                {/* honeypot */}
                <div className="hidden">
                  <label htmlFor="company">Bedrijf</label>
                  <input id="company" type="text" value={formData.company} onChange={handleChange} tabIndex={-1} autoComplete="off" />
                </div>

                {/* Plan select */}
                <div className="mb-6">
                  <p className="block text-gray-700 font-medium mb-3">Kies je plan</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Trainingsplan */}
                    <label className={`block bg-white cursor-pointer border rounded-lg p-4 hover:shadow transition ${formData.plan === "TRAINING" ? "border-primary-red ring-2 ring-primary-red" : "border-gray-300"}`}style={{ cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="plan"
                        id="plan"
                        className="hidden"
                        value="TRAINING"
                        checked={formData.plan === "TRAINING"}
                        onChange={(e) => setFormData((p) => ({ ...p, plan: "TRAINING" }))}
                      />
                      <div className="flex flex-col">
                        <span className="font-bold text-black">Trainingsplan</span>
                        <span className="text-gray-700">Op maat plan</span>
                        <span className="mt-2 font-bold text-black">€{PRICES.TRAINING}</span>
                      </div>
                    </label>

                    {/* Voedingsplan */}
                    <label className={`block bg-white cursor-pointer border rounded-lg p-4 hover:shadow transition ${formData.plan === "FOOD" ? "border-primary-red ring-2 ring-primary-red" : "border-gray-300"}`}style={{ cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="plan2"
                        className="hidden"
                        value="FOOD"
                        checked={formData.plan === "FOOD"}
                        onChange={(e) => setFormData((p) => ({ ...p, plan: "FOOD" }))}
                      />
                      <div className="flex flex-col">
                        <span className="font-bold text-black">Voedingsplan</span>
                        <span className="text-gray-700">Op maat plan</span>
                        <span className="mt-2 font-bold text-black">€{PRICES.FOOD}</span>
                      </div>
                    </label>

                    {/* Bundel */}
                    <label className={`block bg-white cursor-pointer border rounded-lg p-4 hover:shadow transition ${formData.plan === "BUNDLE" ? "border-primary-red ring-2 ring-primary-red" : "border-gray-300"}`}style={{ cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="plan3"
                        className="hidden"
                        value="BUNDLE"
                        checked={formData.plan === "BUNDLE"}
                        onChange={(e) => setFormData((p) => ({ ...p, plan: "BUNDLE" }))}
                      />
                      <div className="flex flex-col">
                        <span className="font-bold text-black">Trainingsplan + voedingsplan bundel</span>
                        <span className="text-gray-700">Combinatiepakket</span>
                        <span className="mt-2 font-bold text-black">€{PRICES.BUNDLE}</span>
                        <span className="text-sm text-green-700 mt-1">Bespaart €19</span>
                      </div>
                    </label>
                  </div>
                  {errors.plan && <p className="mt-2 text-sm text-red-600">{errors.plan}</p>}
                </div>

                {/* Coupon */}
                <div className="mb-6">
                  <label htmlFor="coupon" className="block text-gray-700 font-medium mb-2">
                    Kortingscode
                  </label>
                  <div className="flex gap-3">
                    <input
                      id="coupon"
                      type="text"
                      className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-primary-red border-gray-300 uppercase"
                      placeholder="Voer je kortingscode in en kies 'toepassen'"
                      value={formData.coupon}
                      onChange={handleChange}
                    />
                    <button
                      className="bg-primary-red text-white px-6 py-3 rounded-[10px] font-bold hover:bg-red-600"
                      onClick={applyCoupon}
                    >
                      Toepassen
                    </button>
                  </div>
                  {couponApplied && !couponError && (
                    <p className="mt-2 text-green-700">
                      Code <span className="font-bold">{COUPON}</span> toegepast: –€
                      {formData.plan === "BUNDLE" ? "20" : "10"}
                    </p>
                  )}
                  {couponError && <p className="mt-2 text-red-600">{couponError}</p>}
                </div>

                {/* Contact fields */}
                <div className="mb-5">
                  <label htmlFor="name" className="block text-gray-700 font-medium mb-2">Naam</label>
                  <input
                    id="name"
                    type="text"
                    className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-primary-red ${errors.name ? "border-red-500" : "border-gray-300"}`}
                    placeholder="Jouw naam"
                    value={formData.name}
                    onChange={handleChange}
                    autoComplete="name"
                    required
                    aria-invalid={!!errors.name}
                  />
                  {errors.name && <p className="mt-2 text-sm text-red-600">{errors.name}</p>}
                </div>

                <div className="mb-5">
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-2">E-mail</label>
                  <input
                    id="email"
                    type="email"
                    className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-primary-red ${errors.email ? "border-red-500" : "border-gray-300"}`}
                    placeholder="Jouw e-mail"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                    required
                    aria-invalid={!!errors.email}
                  />
                  {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email}</p>}
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-gray-700 font-medium mb-2">Bericht</label>
                  <textarea
                    id="message"
                    rows={5}
                    className={`w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-primary-red ${errors.message ? "border-red-500" : "border-gray-300"}`}
                    placeholder="Waarmee kan ik je helpen?"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    aria-invalid={!!errors.message}
                  />
                  {errors.message && <p className="mt-2 text-sm text-red-600">{errors.message}</p>}
                </div>

                {/* Summary */}
                <div className="mb-6 rounded-lg border border-gray-200 p-4 bg-gray-50">
                  <div className="flex justify-between">
                    <span>Prijs</span>
                    <span>€{price}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Korting {couponApplied ? `(code: ${COUPON})` : ""}</span>
                    <span className={couponApplied ? "text-green-700" : "text-gray-500"}>
                      –€{discount}
                    </span>
                  </div>
                  <hr className="my-3" />
                  <div className="flex justify-between font-bold text-black">
                    <span>Totaal</span>
                    <span>€{total}</span>
                  </div>
                </div>

                <button
                  type="submit"
                  className="bg-primary-red text-white px-6 py-3 rounded-[10px] font-bold hover:bg-red-600 disabled:bg-gray-400 w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Aanvraag versturen..." : "Aanvragen"}
                </button>
              </form>
            </div>
          </div>

          {/* Visual side */}
          <div className="col-span-12 md:col-span-4 md:block hidden">
            <div className="relative h-full min-h-[320px] rounded-lg overflow-hidden">
              <img
                src="/img/website/hlp-stef-actie.jpg"
                alt="Healthy Living Project"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-0 right-0 bg-white text-black p-4 text-center">
                <p className="text-h4 font-bold md:text-[32px] leading-none">Let's go</p>
                <p className="text-xs font-bold">Geen excuses</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}